// src/pages/LitThemeSection.jsx
//
// Student-facing render of a THEME lit_section (sectionKind 'theme'). Faithful port of the
// prototype LitThemeView (between-the-lines-v2.html L12685-12792): a 5-tab view --
// Introduction / Development / Key Quotes / Sample Analysis / Exam Questions.
//
// Reads the flat THEME_BLOCKS (litSectionShape.js, mirrors LitThemeEditor):
// blocks.intro (longText), blocks.importance (longText), blocks.development
// ([{stage,act,detail}]), blocks.quotes ([{act,quote,analysis}]), blocks.sample (object
// {question,l3,l4,l5}), blocks.exams (string[]).
//
// Pure presentational (takes `content`). Deferred like the Overview port: glossary
// highlighting (renderLitText -> plain text), Export PDF, podcast player, and the quiz tab.
import React, { useState } from 'react';
import AnnotatedText from '../components/AnnotatedText.jsx';
import LessonHeader from '../components/reader/LessonHeader.jsx';

const TABS = [
    ['intro', 'Introduction'],
    ['dev', 'Development'],
    ['quotes', 'Key Quotes'],
    ['sample', 'Sample Analysis'],
    ['exams', 'Exam Questions'],
];

function EmptyNote() {
    return <p className="font-serif italic text-book-muted text-sm">Nothing here yet.</p>;
}

export default function LitThemeSection({ content, done = false }) {
    const blocks = (content && content.blocks) || {};
    const intro = typeof blocks.intro === 'string' ? blocks.intro : '';
    const importance = typeof blocks.importance === 'string' ? blocks.importance : '';
    const development = Array.isArray(blocks.development) ? blocks.development : [];
    const quotes = Array.isArray(blocks.quotes) ? blocks.quotes : [];
    const sample = (blocks.sample && typeof blocks.sample === 'object') ? blocks.sample : null;
    const exams = Array.isArray(blocks.exams) ? blocks.exams : [];
    const [tab, setTab] = useState('intro');
    const [level, setLevel] = useState('l4');

    return (
        <div className="max-w-3xl mx-auto pb-8">
            <LessonHeader eyebrow="Theme" title={(content && content.title) || 'Theme'}
                blocks={blocks} done={done} />

            <div className="inline-flex border border-book-accent/40 mb-6 rounded-sm overflow-hidden flex-wrap">
                {TABS.map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)}
                        className={`px-4 py-2 font-display text-xs transition-colors font-semibold ${tab === k ? 'bg-book-accent text-book-pageDark' : 'text-book-muted hover:text-book-accent'}`}>
                        {l}
                    </button>
                ))}
            </div>

            {tab === 'intro' && (
                (intro || importance) ? (
                    <div className="space-y-5">
                        {intro && <p className="font-serif text-book-text text-base leading-relaxed whitespace-pre-wrap"><AnnotatedText>{intro}</AnnotatedText></p>}
                        {importance && (
                            <div className="border-l-4 border-book-accent bg-book-accent/5 p-5">
                                <div className="font-display text-book-accent text-xs mb-2 font-semibold">Why it matters</div>
                                <p className="font-serif italic text-book-text leading-relaxed whitespace-pre-wrap"><AnnotatedText>{importance}</AnnotatedText></p>
                            </div>
                        )}
                    </div>
                ) : <EmptyNote />
            )}

            {tab === 'dev' && (
                development.length > 0 ? (
                    <div className="space-y-3">
                        <p className="font-serif italic text-book-muted mb-2">How this theme builds across the play.</p>
                        {development.map((d, i) => (
                            <div key={i} className="flex items-stretch gap-4">
                                <div className="w-10 h-10 rounded-full border-2 border-book-accent bg-book-pageDark flex items-center justify-center flex-shrink-0">
                                    <span className="font-display text-book-accent text-sm">{i + 1}</span>
                                </div>
                                <div className="flex-grow rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4">
                                    <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
                                        <div className="font-display text-book-accent text-sm font-semibold">{d && d.stage}</div>
                                        <div className="font-display text-book-muted text-xs font-semibold">{d && d.act}</div>
                                    </div>
                                    <p className="font-serif text-book-text leading-relaxed whitespace-pre-wrap">{d && d.detail}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : <EmptyNote />
            )}

            {tab === 'quotes' && (
                quotes.length > 0 ? (
                    <div className="space-y-3">
                        {quotes.map((q, i) => (
                            <div key={i} className="border-l-4 border-book-accent bg-book-pageDark/40 p-5">
                                <div className="font-display text-book-accent text-xs mb-2 font-semibold">{q && q.act}</div>
                                <p className="font-serif italic text-book-text text-base leading-relaxed mb-2">"{q && q.quote}"</p>
                                <p className="font-serif text-book-text/80 text-sm leading-relaxed">→ <AnnotatedText>{q && q.analysis}</AnnotatedText></p>
                            </div>
                        ))}
                    </div>
                ) : <EmptyNote />
            )}

            {tab === 'sample' && (
                sample && (sample.question || sample.l3 || sample.l4 || sample.l5) ? (
                    <div>
                        <div className="border-2 border-book-accent/50 bg-book-pageDark/60 p-5 mb-5">
                            <div className="font-display text-book-muted text-xs mb-2 font-semibold">Exam question</div>
                            <p className="font-display text-book-text text-base leading-relaxed font-semibold">{sample.question}</p>
                        </div>
                        <div className="inline-flex border border-book-accent/40 mb-4 rounded-sm overflow-hidden">
                            {['l3', 'l4', 'l5'].map((l) => (
                                <button key={l} onClick={() => setLevel(l)}
                                    className={`px-4 py-2 font-display text-xs transition-colors font-semibold ${level === l ? 'bg-book-accent text-book-pageDark' : 'text-book-muted hover:text-book-accent'}`}>
                                    {l.toUpperCase()}
                                </button>
                            ))}
                        </div>
                        <div className="rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4">
                            <div className="font-display text-book-muted text-xs mb-3 font-semibold">Level {level.slice(1).toUpperCase()} response</div>
                            <p className="font-serif text-book-text text-base leading-relaxed text-justify whitespace-pre-wrap">{sample[level]}</p>
                        </div>
                    </div>
                ) : <EmptyNote />
            )}

            {tab === 'exams' && (
                exams.length > 0 ? (
                    <div className="space-y-2">
                        <p className="font-serif italic text-book-muted mb-3">Common exam questions on this theme.</p>
                        {exams.map((e, i) => (
                            <div key={i} className="rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4 font-serif text-book-text">
                                <span className="font-display text-book-accent text-xs mr-2 font-semibold">Q{i + 1}</span>{e}
                            </div>
                        ))}
                    </div>
                ) : <EmptyNote />
            )}
        </div>
    );
}
